// export default FindImmatriculationOrChasisNumberScreen;
import React, { useContext, useState, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Modal,
  Pressable,
} from "react-native";
import axios from "axios";
import { uploadVerificationDocument } from "../../api/verificationApi";
import { useNavigation } from "@react-navigation/native";
import { AppContext } from "../../../App";
import { useFormNavigation } from "../../utils/navigationUtils";
import Header from "../../components/Header";
import { useTranslation } from "../../i18n";
import { getPaymentCompletionStatus } from "../../services/paymentService";
// import DriverMatchModal from "../../components/DriverMatchModal";

const API_BASE_URL = "https://api.taxi-yaounde.com";
// const API_BASE_URL = "http://localhost:4000";

const COLORS = {
  primary: "#14B8A6",
  background: "#F7F9FA",
  card: "#FFFFFF",
  textDark: "#1C1C1E",
  textMuted: "#6B7280",
  success: "#16A34A",
  error: "#DC2626",
  border: "#E5E7EB",
};

// ---------- name helpers (fuzzy matching) ----------
const normalizeName = (str) =>
  (str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const levenshtein = (a, b) => {
  const s = a;
  const t = b;
  const m = s.length;
  const n = t.length;
  if (m === 0) return n;
  if (n === 0) return m;

  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }
  return dp[m][n];
};

const isFuzzyMatch = (a, b) => {
  const A = normalizeName(a);
  const B = normalizeName(b);
  if (!A || !B) return false;
  if (A === B) return true;
  if (A.includes(B) || B.includes(A)) return true;

  const dist = levenshtein(A, B);
  const maxLen = Math.max(A.length, B.length);
  const ratio = dist / maxLen;
  return ratio <= 0.2;
};



const FindImmatriculationOrChasisNumberScreen = () => {
  const navigation = useNavigation();
  const { verification, setVerification, setFormProgress } =
    useContext(AppContext);
  const { updateCurrentStep } = useFormNavigation();
  const { t } = useTranslation();

  const [input, setInput] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [nameMatches, setNameMatches] = useState(false);
  const [showNotFoundModal, setShowNotFoundModal] = useState(false);
  const [searchedValue, setSearchedValue] = useState("");
  const [matchedOwner, setMatchedOwner] = useState(null);
  const [allOwners, setAllOwners] = useState([]);
  const [matchedDriver, setMatchedDriver] = useState(null);
  const [showDriverMatchModal, setShowDriverMatchModal] = useState(false);

  // ✅ NEW: live input validity (controls Search enabled state)
  const [isInputValid, setIsInputValid] = useState(false);

  // New loading states for modal buttons
  const [isContinuingVerification, setIsContinuingVerification] = useState(false);
  const [isRegisteringFresh, setIsRegisteringFresh] = useState(false);

  useEffect(() => {
    updateCurrentStep("find_immatriculation_or_chasis_number_screen");
  }, []);

  // ✅ Save to context (debounced) — NO SPACES allowed
  useEffect(() => {
    const timer = setTimeout(() => {
      const cleaned = (input || "").replace(/\s+/g, "");
      if (cleaned) {
        setVerification((p) => ({
          ...p,
          numeimma: cleaned.toUpperCase(),
        }));
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [input]);

  // ✅ Validation (no side effects)
  const validateInput = (raw) => {
    const plate = String(raw || "").trim().toUpperCase();

    const regionCodes = new Set([
      "AD",
      "CE",
      "EN",
      "ES",
      "LT",
      "NO",
      "NW",
      "OU",
      "SU",
      "SW",
    ]);

    const isOldPlateFormat =
      plate.length === 7 &&
      regionCodes.has(plate.substring(0, 2)) &&
      /^\d{4}$/.test(plate.substring(2, 6)) &&
      /[A-Z]/.test(plate.substring(6));

    const isNewPlateFormat =
      plate.length === 7 &&
      regionCodes.has(plate.substring(0, 2)) &&
      /^\d{3}$/.test(plate.substring(2, 5)) &&
      /^[A-Z]{2}$/.test(plate.substring(5, 7));

    const isChassisSixDigitFormat =
      plate.startsWith("CH") &&
      plate.length === 8 &&
      /^\d{6}$/.test(plate.substring(2));

    const isFullChassis = plate.length === 17 && /^[A-Z0-9]+$/.test(plate);

    return (
      isOldPlateFormat ||
      isNewPlateFormat ||
      isChassisSixDigitFormat ||
      isFullChassis
    );
  };

  // ✅ Live validity (controls button)
  useEffect(() => {
    const cleaned = (input || "").replace(/\s+/g, "");
    const ok = validateInput(cleaned);
    setIsInputValid(ok);

    // show invalid format message only when user typed something
    if (cleaned.length > 0 && !ok) setErrorMessage(t("verification.invalidFormat"));
    else setErrorMessage(null);
  }, [input, t]);

  // Function to check if driver matches any existing driver for the vehicle
const checkDriverMatch = (vehicleData, driverName) => {
  if (!vehicleData?.vehicle_drivers || !driverName) {
    return null;
  }
  
  const normalizedDriverName = normalizeName(driverName);
  console.log("Checking driver match for: - FindImmatriculationOrChasisNumber.js:195", driverName, "Normalized:", normalizedDriverName);
  console.log("Vehicle drivers: - FindImmatriculationOrChasisNumber.js:196", vehicleData.vehicle_drivers);
  
  // Check all drivers for this vehicle
  for (const driver of vehicleData.vehicle_drivers) {
    if (driver.name) {
      const isMatch = isFuzzyMatch(driver.name, normalizedDriverName);
      console.log(`Comparing "${driver.name}" with "${driverName}": ${isMatch} - FindImmatriculationOrChasisNumber.js:202`);
      if (isMatch) {
        return driver; // Return the matched driver
      }
    }
  }
  
  return null; // No match found
};

  const handleSearch = async () => {
    setResult(null);
    setNameMatches(false);
     setMatchedDriver(null);

    const cleaned = (input || "").replace(/\s+/g, "").trim().toUpperCase();
    if (!validateInput(cleaned)) {
      setErrorMessage(t("verification.invalidFormat"));
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const immatric = cleaned;
      setSearchedValue(immatric);

      const response = await axios.post(
        `${API_BASE_URL}/api/verification/find-immatriculation`,
        { input: immatric }
      );

      const payload = response.data; // { found: true/false, data: {...} }

      if (!payload?.found) {
        setShowNotFoundModal(true);
        return;
      }

      const row = payload.data;
      setResult(row);
    // Get all owners from the response
    const allPossibleOwners = row?.all_possible_owners || [];
    const vehicleOwners = row?.vehicle_owners || [];
    const correctedOwners = row?.corrected_owners || [];
  
  setAllOwners(allPossibleOwners);
      const assureFromDb = row?.ASSURE || "";
  
  setAllOwners(allPossibleOwners);

      const ownerInfo = verification?.owner;
      if (!ownerInfo) {
        console.log(
          "No ownerInfo in context - FindImmatriculationOrChasisNumber.js"
        );
        setNameMatches(false);
        setMatchedOwner(null);
        setShowModal(true);
        return;
      }

      let enteredOwnerName = "";
      const ownerType = (ownerInfo.type || "").toUpperCase();

      if (ownerType === "PHYSIQUE") {
        enteredOwnerName = `${ownerInfo.firstName || ""} ${ownerInfo.lastName || ""}`.trim();
      } else if (ownerType === "MORALE") {
        enteredOwnerName = (ownerInfo.organizationName || "").trim();
      }

      console.log("assure: - FindImmatriculationOrChasisNumber.js:274", assureFromDb);
      console.log("ownerInfo: - FindImmatriculationOrChasisNumber.js:275", ownerInfo);
      console.log("enteredOwnerName: - FindImmatriculationOrChasisNumber.js:276", enteredOwnerName);

      // const match =
      //   !!enteredOwnerName && isFuzzyMatch(assureFromDb, enteredOwnerName);

      // const allPossibleOwners = row?.all_possible_owners || [];
      // const vehicleOwners = row?.vehicle_owners || [];
      // const correctedOwners = row?.corrected_owners || [];
  
  // setAllOwners(allPossibleOwners);
      let foundMatch = null;
      for (const owner of allPossibleOwners) {
        if (!!enteredOwnerName && isFuzzyMatch(owner.name, enteredOwnerName)) {
          foundMatch = owner;
          break;
        }
      }

      if (foundMatch) {
        setNameMatches(true);
        setMatchedOwner(foundMatch);
      } else {
        setNameMatches(false);
        setMatchedOwner(null);
      }

          // ✅ NEW: Check if this is a driver workflow
    const isDriverWorkflow = verification?.actorRole === "DRIVER";
    
    if (isDriverWorkflow) {
      // Get driver name from context (entered in NotVehicleOwnerForm)
      const driverName = verification?.driver 
        ? `${verification.driver.firstName || ""} ${verification.driver.lastName || ""}`.trim()
        : "";
      
      console.log("Driver workflow  checking if driver exists: - FindImmatriculationOrChasisNumber.js:311", driverName);
      
      // Check if this driver is already attached to the vehicle
      const matchedDriver = checkDriverMatch(row, driverName);
      
      if (matchedDriver) {
        console.log("✅ Driver match found: - FindImmatriculationOrChasisNumber.js:317", matchedDriver);
          setVerification(prev => ({
            ...prev,
            driver: {
              ...prev.driver,
              personId: matchedDriver.person_id,
              fullName: matchedDriver.name,
              phone: matchedDriver.phone,
              licenseNumber: matchedDriver.license_number,
              isMatched: true,
            },
            matchedDriverData: matchedDriver,
            driverMatched: true,
          }));
        setMatchedDriver(matchedDriver);
        setShowDriverMatchModal(true); // Show success modal
      } else {
        console.log("❌ No driver match found - FindImmatriculationOrChasisNumber.js:334");
        setMatchedDriver(null);
      }
    }


      setNameMatches(foundMatch !== null);
      setShowModal(true);

      setFormProgress((prev) => ({
        ...prev,
        completedSteps: [
          ...new Set([
            ...(prev.completedSteps || []),
            "find_immatriculation_or_chasis_number_screen",
          ]),
        ],
      }));
    } catch (e) {
      console.log(
        "Erreur backend /api/verification/findimmatriculation:",
        e
      );
      setResult(null);
      setShowModal(false);
      setErrorMessage(t("verification.networkError"));
    } finally {
      setIsLoading(false);
    }
  };

  const immatriculation = result?.NUMEIMMA ?? "";
  const assure = result?.ASSURE ?? "";
  const dateEcheance = result?.DATEECHE ?? "";


//   const handleContinueVerification = async () => {
//   setIsContinuingVerification(true);

//   const owner = verification?.owner;

//   // Check owner completeness
//   const ownerIsIncomplete =
//     !owner ||
//     ((owner.type || "").toUpperCase() === "PHYSIQUE" &&
//       (!owner.firstName || !owner.lastName)) ||
//     ((owner.type || "").toUpperCase() === "MORALE" && !owner.organizationName);

//   if (ownerIsIncomplete) {
//     setErrorMessage(t("verification.ownerInfoIncomplete"));
//     setShowModal(false);
//     setIsContinuingVerification(false);
//     return;
//   }

//   try {
//     console.log(
//       `Owner info being sent to backend is ${JSON.stringify(verification.owner)}`
//     );

//     const resp = await axios.post(`${API_BASE_URL}/api/verification/start`, {
//       numeimma: (immatriculation || "").trim().toUpperCase(),
//       owner: verification.owner,
//       driver: verification.driver || null,
//     });

//     const data = resp.data;

//     if (!data?.success || !data?.verificationId) {
//       setShowModal(false);
//       setErrorMessage(data?.error || t("verification.serverResponseInvalid"));
//       setIsContinuingVerification(false);
//       return;
//     }

//     setVerification((prev) => ({
//       ...prev,
//       verificationId: data.verificationId,
//       vehicleId: data.vehicleId ?? prev.vehicleId ?? null,
//       numeimma: (immatriculation || "").trim().toUpperCase(),
//     }));

//     // ✅ UPLOAD ALL DOCUMENTS
//     try {
//       // 1. Check if this is driver workflow
//       const isDriverWorkflow = verification?.actorRole === "DRIVER";
      
//       // 2. Upload owner ID card if present (for owner workflow)
//       const pendingOwnerId = verification?.pendingUploads?.ownerId;
//       if (pendingOwnerId) {
//         await uploadVerificationDocument(
//           data.verificationId,
//           "OWNER_ID",
//           pendingOwnerId
//         );

//         setVerification((prev) => ({
//           ...prev,
//           pendingUploads: {
//             ...(prev.pendingUploads || {}),
//             ownerId: null,
//           },
//         }));
//       }

//       // 3. Upload PROCURATION if it exists (for driver workflow)
//       // Check both places where the file might be stored
//       const procurationFile = verification?.pendingUploads?.procurationFile || 
//                              verification?.owner?.procurationFile;
      
//       if (isDriverWorkflow && procurationFile && 
//           !verification?.owner?.skipProcuration) {
        
//         console.log("Uploading procuration document... - FindImmatriculationOrChasisNumber.js:432");
//         await uploadVerificationDocument(
//           data.verificationId,
//           "PROCURATION",  // Make sure backend uses this docType
//           procurationFile
//         );
        
//         // Mark procuration as uploaded
//         setVerification(prev => ({ 
//           ...prev, 
//           hasProcuration: true 
//         }));
        
//         // Clear the file from pending uploads
//         setVerification((prev) => ({
//           ...prev,
//           pendingUploads: {
//             ...(prev.pendingUploads || {}),
//             procurationFile: null,
//           },
//         }));
        
//         console.log("Procuration document uploaded successfully - FindImmatriculationOrChasisNumber.js:454");
//       } else if (isDriverWorkflow && verification?.owner?.skipProcuration) {
//         console.log("Procuration document skipped by user - FindImmatriculationOrChasisNumber.js:456");
//         setVerification(prev => ({ 
//           ...prev, 
//           hasProcuration: false 
//         }));
//       }
      
//       // 4. If it's owner workflow and no procuration, set hasProcuration to false
//       if (!isDriverWorkflow) {
//         setVerification(prev => ({ 
//           ...prev, 
//           hasProcuration: false 
//         }));
//       }
      
//     } catch (uploadError) {
//       console.log("Document upload failed: - FindImmatriculationOrChasisNumber.js:472", uploadError?.message || uploadError);
//       // Decide whether to continue without documents or show error
//       // For now, we'll continue but log the error
//       // You might want to show a warning to the user
//     }

//     // Check payment completion status
//     const completionStatus = await getPaymentCompletionStatus(data.verificationId);
//     if (completionStatus.completed) {
//       console.log("✅ Payment already completed, skipping to vehicle admin info - FindImmatriculationOrChasisNumber.js:481");

//       setFormProgress((prev) => ({
//         ...prev,
//         completedSteps: [
//           ...new Set([
//             ...(prev.completedSteps || []),
//             "find_immatriculation_or_chasis_number_screen",
//             "payment_screen",
//           ]),
//         ],
//         currentStep: "vehicle_admin_info",
//       }));

//       setShowModal(false);
//       setIsContinuingVerification(false);

//       navigation.navigate("vehicle_admin_info", {
//         verificationId: data.verificationId,
//         vehicleId: data.vehicleId,
//         status: data.status,
//       });
//     } else {
//       setFormProgress((prev) => ({
//         ...prev,
//         completedSteps: [
//           ...new Set([
//             ...(prev.completedSteps || []),
//             "find_immatriculation_or_chasis_number_screen",
//           ]),
//         ],
//         currentStep: "payment_screen",
//       }));

//       setShowModal(false);
//       setIsContinuingVerification(false);

//       navigation.navigate("payment_screen", {
//         verificationId: data.verificationId,
//         vehicleId: data.vehicleId,
//       });
//     }
//   } catch (e) {
//     console.log("Error /api/verification/start: - FindImmatriculationOrChasisNumber.js:524", e);
//     setShowModal(false);
//     setIsContinuingVerification(false);

//     const msg =
//       e?.response?.data?.message ||
//       e?.response?.data?.error ||
//       t("verification.serverError");

//     setErrorMessage(msg);
//   }
// };

  // Update handleContinueVerification to pass matchedDriver to next screen
  const handleContinueVerification = async () => {
    setIsContinuingVerification(true);

    const owner = verification?.owner;

    const ownerIsIncomplete =
      !owner ||
      ((owner.type || "").toUpperCase() === "PHYSIQUE" &&
        (!owner.firstName || !owner.lastName)) ||
      ((owner.type || "").toUpperCase() === "MORALE" && !owner.organizationName);

    if (ownerIsIncomplete) {
      setErrorMessage(t("verification.ownerInfoIncomplete"));
      setShowModal(false);
      setIsContinuingVerification(false);
      return;
    }

    try {
      console.log(
        `Owner info being sent to backend is ${JSON.stringify(verification.owner)}`
      );

      const resp = await axios.post(`${API_BASE_URL}/api/verification/start`, {
        numeimma: (immatriculation || "").trim().toUpperCase(),
        owner: verification.owner,
        driver: verification.driver || null,
      });

      const data = resp.data;

      if (!data?.success || !data?.verificationId) {
        setShowModal(false);
        setErrorMessage(data?.error || t("verification.serverResponseInvalid"));
        setIsContinuingVerification(false);
        return;
      }

      // setVerification((prev) => ({
      //   ...prev,
      //   verificationId: data.verificationId,
      //   vehicleId: data.vehicleId ?? prev.vehicleId ?? null,
      //   numeimma: (immatriculation || "").trim().toUpperCase(),
      // }));
      setVerification((prev) => ({
        ...prev,
        verificationId: data.verificationId,
        vehicle: {
          ...prev.vehicle,
          vehicleId: data.vehicleId ?? prev.vehicle?.vehicleId ?? null,
        },
        numeimma: (immatriculation || "").trim().toUpperCase(),
      }));

      // Upload documents (existing code)...
      try {
        // 1. Check if this is driver workflow
        const isDriverWorkflow = verification?.actorRole === "DRIVER";
        
        // 2. Upload owner ID card if present
        const pendingOwnerId = verification?.pendingUploads?.ownerId;
        if (pendingOwnerId) {
          await uploadVerificationDocument(
            data.verificationId,
            "OWNER_ID",
            pendingOwnerId
          );

          setVerification((prev) => ({
            ...prev,
            pendingUploads: {
              ...(prev.pendingUploads || {}),
              ownerId: null,
            },
          }));
        }

        // 3. Upload PROCURATION if it exists (for driver workflow)
        const procurationFile = verification?.pendingUploads?.procurationFile || 
                              verification?.owner?.procurationFile;
        
        if (isDriverWorkflow && procurationFile && 
            !verification?.owner?.skipProcuration) {
          
          console.log("Uploading procuration document... - FindImmatriculationOrChasisNumber.js:637");
          await uploadVerificationDocument(
            data.verificationId,
            "PROCURATION",
            procurationFile
          );
          
          setVerification(prev => ({ 
            ...prev, 
            hasProcuration: true 
          }));
          
          setVerification((prev) => ({
            ...prev,
            pendingUploads: {
              ...(prev.pendingUploads || {}),
              procurationFile: null,
            },
          }));
          
          console.log("Procuration document uploaded successfully - FindImmatriculationOrChasisNumber.js:657");
        } else if (isDriverWorkflow && verification?.owner?.skipProcuration) {
          console.log("Procuration document skipped by user - FindImmatriculationOrChasisNumber.js:659");
          setVerification(prev => ({ 
            ...prev, 
            hasProcuration: false 
          }));
        }
        
        if (!isDriverWorkflow) {
          setVerification(prev => ({ 
            ...prev, 
            hasProcuration: false 
          }));
        }
        
      } catch (uploadError) {
        console.log("Document upload failed: - FindImmatriculationOrChasisNumber.js:674", uploadError?.message || uploadError);
      }

      // Check payment completion status
      const completionStatus = await getPaymentCompletionStatus(data.verificationId);
      if (completionStatus.completed) {
        console.log("✅ Payment already completed, skipping to vehicle admin info - FindImmatriculationOrChasisNumber.js:680");

        setFormProgress((prev) => ({
          ...prev,
          completedSteps: [
            ...new Set([
              ...(prev.completedSteps || []),
              "find_immatriculation_or_chasis_number_screen",
              "payment_screen",
            ]),
          ],
          currentStep: "vehicle_admin_info",
        }));

        setShowModal(false);
        setIsContinuingVerification(false);

        // ✅ PASS matchedDriver to vehicle_admin_info screen
        navigation.navigate("vehicle_admin_info", {
          verificationId: data.verificationId,
          vehicleId: data.vehicleId,
          status: data.status,
          matchedDriver: matchedDriver, // Pass the matched driver if any
          isDriverWorkflow: verification?.actorRole === "DRIVER",
        });
      } else {
        setFormProgress((prev) => ({
          ...prev,
          completedSteps: [
            ...new Set([
              ...(prev.completedSteps || []),
              "find_immatriculation_or_chasis_number_screen",
            ]),
          ],
          currentStep: "payment_screen",
        }));

        setShowModal(false);
        setIsContinuingVerification(false);

        navigation.navigate("payment_screen", {
          verificationId: data.verificationId,
          vehicleId: data.vehicleId,
          matchedDriver: matchedDriver, // ✅ Pass it to payment screen too
          isDriverWorkflow: verification?.actorRole === "DRIVER",
        });
      }
    } catch (e) {
      console.log("Error /api/verification/start: - FindImmatriculationOrChasisNumber.js:728", e);
      setShowModal(false);
      setIsContinuingVerification(false);

      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        t("verification.serverError");

      setErrorMessage(msg);
    }
  };

  const handleGoBack = () => {
    const ownerType = (verification?.owner?.type || "").toUpperCase();

    if (ownerType === "PHYSIQUE" || ownerType === "MORALE") {
      navigation.navigate("vehicle_owner_form_screen");
    } else {
      navigation.navigate("not_vehicle_owner_form_screen");
    }
  };

  const handleRegisterAfresh = async () => {
    setIsRegisteringFresh(true);
    try {
      const plateOrChassis = (searchedValue || input || "")
        .replace(/\s+/g, "")
        .trim()
        .toUpperCase();

      setShowModal(false);
      setShowNotFoundModal(false);

      navigation.navigate("payment_screen", {
        flow: "NEW_REGISTRATION_PREPAY",
        identifier: plateOrChassis,
        matchedDriver: matchedDriver,
        isDriverWorkflow: verification?.actorRole === "DRIVER",
      });
    } finally {
      setIsRegisteringFresh(false);
    }
  };

// const handleRegisterNOtMatchedUser = async () => {
//   const plateOrChassis = (searchedValue || input || "")
//     .replace(/\s+/g, "")
//     .trim()
//     .toUpperCase();

//   // Get the owner info that the user entered earlier (from context)
//   const ownerInfo = verification?.owner;
//   let enteredOwnerName = "";

//   if (ownerInfo) {
//     const ownerType = (ownerInfo.type || "").toUpperCase();
    
//     if (ownerType === "PHYSIQUE") {
//       enteredOwnerName = `${ownerInfo.firstName || ""} ${ownerInfo.lastName || ""}`.trim();
//     } else if (ownerType === "MORALE") {
//       enteredOwnerName = (ownerInfo.organizationName || "").trim();
//     }
//   }

//   setShowModal(false);
  
//   // Pass the user's entered info, not the search result
//   navigation.navigate("vehicle_not_matched_info", {
//     identifier: plateOrChassis,
//     existingOwnerName: enteredOwnerName || "", // User's entered name
//     userEnteredOwnerInfo: ownerInfo, // Pass the entire owner object for reference
//     searchResultOwnerName: result?.ASSURE || "", // Keep search result for comparison if needed
//   });
// };


const handleRegisterNOtMatchedUser = async () => {
  setIsContinuingVerification(true);

  const plateOrChassis = (searchedValue || input || "")
    .replace(/\s+/g, "")
    .trim()
    .toUpperCase();

  // owner info from context
  const ownerInfo = verification?.owner;

  let enteredOwnerName = "";
  if (ownerInfo) {
    const ownerType = (ownerInfo.type || "").toUpperCase();
    if (ownerType === "PHYSIQUE") {
      enteredOwnerName = `${ownerInfo.firstName || ""} ${ownerInfo.lastName || ""}`.trim();
    } else if (ownerType === "MORALE") {
      enteredOwnerName = (ownerInfo.organizationName || "").trim();
    }
  }

  // basic guard (same as continue verification)
  const ownerIsIncomplete =
    !ownerInfo ||
    ((ownerInfo.type || "").toUpperCase() === "PHYSIQUE" &&
      (!ownerInfo.firstName || !ownerInfo.lastName)) ||
    ((ownerInfo.type || "").toUpperCase() === "MORALE" && !ownerInfo.organizationName);

  if (ownerIsIncomplete) {
    setErrorMessage(t("verification.ownerInfoIncomplete"));
    setShowModal(false);
    setIsContinuingVerification(false);
    return;
  }

  try {
    // ✅ Start (or create) verificationId for this mismatch flow
    const resp = await axios.post(`${API_BASE_URL}/api/verification/start`, {
      numeimma: plateOrChassis,
      owner: ownerInfo,
      driver: verification.driver || null,
    });

    const data = resp.data;

    if (!data?.success || !data?.verificationId) {
      setShowModal(false);
      setErrorMessage(data?.error || t("verification.serverResponseInvalid"));
      setIsContinuingVerification(false);
      return;
    }

    // ✅ store in context (important for payment screen)
    setVerification((prev) => ({
      ...prev,
      verificationId: data.verificationId,
      vehicleId: data.vehicleId ?? prev.vehicleId ?? null,
      numeimma: plateOrChassis,
    }));

    // ✅ mark progress + go to payment screen first
    setFormProgress((prev) => ({
      ...prev,
      completedSteps: [
        ...new Set([
          ...(prev.completedSteps || []),
          "find_immatriculation_or_chasis_number_screen",
        ]),
      ],
      currentStep: "payment_screen",
    }));

    setShowModal(false);
    setIsContinuingVerification(false);

    navigation.navigate("payment_screen", {
      flow: "OWNER_MISMATCH_FLOW",                 // ✅ NEW
      verificationId: data.verificationId,
      vehicleId: data.vehicleId,
      identifier: plateOrChassis,                  // ✅ useful for downstream
      existingOwnerName: enteredOwnerName || "",
      userEnteredOwnerInfo: ownerInfo || null,
      searchResultOwnerName: result?.ASSURE || "",
      matchedDriver: matchedDriver, // ✅ Pass driver info if available
      isDriverWorkflow: verification?.actorRole === "DRIVER",
    });
  } catch (e) {
    console.log("Error mismatch flow /api/verification/start: - FindImmatriculationOrChasisNumber.js:892", e);
    setShowModal(false);
    setIsContinuingVerification(false);

    const msg =
      e?.response?.data?.message ||
      e?.response?.data?.error ||
      t("verification.serverError");

    setErrorMessage(msg);
  }
};

  return (
    <View style={styles.screen}>
      <Header />

      <ScrollView contentContainerStyle={styles.scroll}>
        <Pressable style={styles.backButton} onPress={handleGoBack}>
          <Text style={styles.backIcon}>←</Text>
        </Pressable>

        <Text style={styles.title}>{t("verification.title")}</Text>
        <Text style={styles.subtitle}>{t("verification.subtitle")}</Text>

        <View style={styles.card}>
          <Text style={styles.label}>{t("verification.inputLabel")}</Text>

          <TextInput
            value={input}
            onChangeText={(val) => {
              // ✅ remove ALL spaces instantly
              const cleaned = String(val || "").replace(/\s+/g, "");
              setInput(cleaned);
            }}
            placeholder={t("verification.inputPlaceholder")}
            placeholderTextColor="#9CA3AF"
            autoCapitalize="characters"
            style={[styles.input, errorMessage && { borderColor: COLORS.error }]}
          />

          {errorMessage && <Text style={styles.errorText}>{errorMessage}</Text>}

          <TouchableOpacity
            onPress={handleSearch}
            disabled={!isInputValid || isLoading}
            style={[
              styles.cta,
              (!isInputValid || isLoading) && { opacity: 0.5 },
            ]}
          >
            {isLoading ? (
              <ActivityIndicator color="#FFF" />
            ) : (
              <Text style={styles.ctaText}>{t("verification.searchButton")}</Text>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* <Modal visible={showModal && !!result} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{t("verification.resultFound")}</Text>

            <InfoRow label={t("verification.registration")} value={immatriculation} />
            <InfoRow label={t("verification.insured")} value={assure} />
            <InfoRow label={t("verification.expiry")} value={dateEcheance} />

            {nameMatches ? (
              <>
                <Text style={styles.successText}>{t("verification.infoMatches")}</Text>
                <TouchableOpacity
                  style={[
                    styles.modalPrimary,
                    isContinuingVerification && styles.buttonLoading,
                  ]}
                  onPress={handleContinueVerification}
                  disabled={isContinuingVerification}
                >
                  {isContinuingVerification ? (
                    <ActivityIndicator color="#FFF" size="small" />
                  ) : (
                    <Text style={styles.modalPrimaryText}>
                      {t("verification.continueVerification")}
                    </Text>
                  )}
                </TouchableOpacity>
              </>
            ) : (
              <>
                <Text style={styles.errorTextCenter}>{t("verification.infoDoesNotMatch")}</Text>
                <TouchableOpacity
                  style={styles.modalSecondary}
                  onPress={handleRegisterNOtMatchedUser}
                >
                  <Text style={styles.modalSecondaryText}>{t("common.continue")}</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>
      </Modal> */}

      <Modal visible={showModal && !!result} transparent animationType="fade">
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setShowModal(false)}
        >
          <Pressable
            style={styles.modalCard}
            onPress={(e) => e.stopPropagation()}
          >
            <Text style={styles.modalTitle}>{t("verification.resultFound")}</Text>

            <InfoRow label={t("verification.registration")} value={immatriculation} />

            {nameMatches && matchedOwner && (
              <InfoRow label={t("verification.insured")} value={matchedOwner.name} />
            )}

            <InfoRow label={t("verification.expiry")} value={dateEcheance} />

            {!nameMatches && allOwners.length > 0 && (
              <View style={styles.allOwnersContainer}>
                <Text style={styles.ownersLabel}>
                  {t("verification.registeredOwners")}:
                </Text>

                {allOwners
                  .filter((owner) => owner.source !== "owner_mismatch")
                  .map((owner, index) => (
                    <View
                      key={index}
                      style={[
                        styles.ownerItem,
                        owner.source === "owner_mismatch" && styles.correctedOwnerItem,
                      ]}
                    >
                      <Text style={styles.ownerName}>
                        {owner.source === "owner_mismatch" ? "🔄 " : "👤 "}
                        {owner.name}
                      </Text>
                    </View>
                  ))}
              </View>
            )}

            {nameMatches ? (
              <>
                <Text style={styles.successText}>{t("verification.infoMatches")}</Text>

                <TouchableOpacity
                  style={[
                    styles.modalPrimary,
                    isContinuingVerification && styles.buttonLoading,
                  ]}
                  onPress={handleContinueVerification}
                  disabled={isContinuingVerification}
                >
                  {isContinuingVerification ? (
                    <ActivityIndicator color="#FFF" size="small" />
                  ) : (
                    <Text style={styles.modalPrimaryText}>
                      {t("verification.continueVerification")}
                    </Text>
                  )}
                </TouchableOpacity>
              </>
            ) : (
              <>
                <Text style={styles.errorTextCenter}>
                  {t("verification.infoDoesNotMatch")}
                </Text>

                <TouchableOpacity
                  style={[
                    styles.modalSecondary,
                    isContinuingVerification && { opacity: 0.6 },
                  ]}
                  onPress={handleRegisterNOtMatchedUser}
                  disabled={isContinuingVerification}
                >
                  {isContinuingVerification ? (
                    <ActivityIndicator />
                  ) : (
                    <Text style={styles.modalSecondaryText}>{t("common.continue")}</Text>
                  )}
                </TouchableOpacity>
              </>
            )}
          </Pressable>
        </Pressable>
      </Modal>


      <Modal visible={showNotFoundModal} transparent animationType="fade">
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setShowNotFoundModal(false)}
        >
          <Pressable
            style={styles.modalCard}
            onPress={(e) => e.stopPropagation()}
          >
            <Text style={styles.modalTitle}>{t("verification.vehicleNotFound")}</Text>

            <Text
              style={{
                textAlign: "center",
                color: COLORS.textMuted,
                marginBottom: 14,
              }}
            >
              {t("verification.vehicleNotFoundMessage")}
            </Text>

            <InfoRow label={t("verification.input")} value={searchedValue || input} />

            <TouchableOpacity
              style={[styles.modalPrimary, isRegisteringFresh && styles.buttonLoading]}
              onPress={handleRegisterAfresh}
              disabled={isRegisteringFresh}
            >
              {isRegisteringFresh ? (
                <ActivityIndicator color="#FFF" size="small" />
              ) : (
                <Text style={styles.modalPrimaryText}>{t("common.continue")}</Text>
              )}
            </TouchableOpacity>

            <View style={{ height: 10 }} />

            <TouchableOpacity
              style={styles.modalSecondary}
              onPress={() => setShowNotFoundModal(false)}
              disabled={isRegisteringFresh}
            >
              <Text
                style={[
                  styles.modalSecondaryText,
                  isRegisteringFresh && { opacity: 0.5 },
                ]}
              >
                {t("common.cancel")}
              </Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>


      <Modal visible={showDriverMatchModal} transparent animationType="fade">
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setShowDriverMatchModal(false)}
        >
          <Pressable
            style={styles.modalCard}
            onPress={(e) => e.stopPropagation()}
          >
            <Text style={styles.modalTitle}>✅ {t("verification.driverFound")}</Text>
            
            <Text style={styles.successTextCenter}>
              {t("verification.driverFoundMessage")}
            </Text>
            
            <View style={styles.driverInfoBox}>
              <Text style={styles.driverInfoName}>{matchedDriver?.name}</Text>
              {matchedDriver?.phone && (
                <Text style={styles.driverInfoPhone}>{matchedDriver.phone}</Text>
              )}
              {matchedDriver?.license_number && (
                <Text style={styles.driverInfoLicense}>
                  {t("verification.licenseNumber")}: {matchedDriver.license_number}
                </Text>
              )}
            </View>
            
            <TouchableOpacity
              style={styles.modalPrimary}
              onPress={() => setShowDriverMatchModal(false)}
            >
              <Text style={styles.modalPrimaryText}>{t("common.continue")}</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

    </View>
  );
};

const InfoRow = ({ label, value }) => (
  <View style={styles.infoRow}>
    <Text style={styles.infoLabel}>{label}</Text>
    <Text style={styles.infoValue}>{value || "-"}</Text>
  </View>
);

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: COLORS.background,
  },
  backButton: {
    width: 42,
    height: 42,
    borderRadius: 12,
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
    marginTop: 16,
  },
  backIcon: {
    fontSize: 18,
    color: COLORS.textDark,
  },
  scroll: {
    paddingHorizontal: 16,
    paddingBottom: 40,
  },
  title: {
    fontSize: 24,
    fontWeight: "800",
    textAlign: "center",
    marginBottom: 6,
  },
  subtitle: {
    textAlign: "center",
    color: COLORS.textMuted,
    marginBottom: 28,
  },
  card: {
    backgroundColor: COLORS.card,
    borderRadius: 16,
    padding: 16,
    shadowColor: "#000",
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 3,
  },
  label: {
    fontWeight: "600",
    marginBottom: 6,
  },
  input: {
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 12,
    padding: 14,
    fontSize: 15,
    marginBottom: 8,
  },
  errorText: {
    color: COLORS.error,
    fontSize: 12,
    marginBottom: 8,
  },
  cta: {
    backgroundColor: COLORS.primary,
    paddingVertical: 14,
    borderRadius: 14,
    alignItems: "center",
    marginTop: 8,
  },
  ctaText: {
    color: "#FFF",
    fontSize: 16,
    fontWeight: "700",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  modalCard: {
    width: "100%",
    maxWidth: 520,
    backgroundColor: "#FFF",
    borderRadius: 20,
    padding: 20,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "700",
    textAlign: "center",
    marginBottom: 12,
  },
  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginVertical: 4,
  },
  infoLabel: {
    color: COLORS.textMuted,
  },
  infoValue: {
    fontWeight: "700",
  },
  successText: {
    color: COLORS.success,
    textAlign: "center",
    marginVertical: 12,
    fontWeight: "600",
  },
  errorTextCenter: {
    color: COLORS.error,
    textAlign: "center",
    marginVertical: 12,
    fontWeight: "600",
  },
  modalPrimary: {
    backgroundColor: COLORS.primary,
    paddingVertical: 12,
    borderRadius: 14,
    alignItems: "center",
    minHeight: 44,
    justifyContent: "center",
  },
  modalPrimaryText: {
    color: "#FFF",
    fontWeight: "700",
    fontSize: 16,
  },
  modalSecondary: {
    backgroundColor: "#F3F4F6",
    paddingVertical: 12,
    borderRadius: 14,
    alignItems: "center",
    minHeight: 44,
    justifyContent: "center",
  },
  modalSecondaryText: {
    fontWeight: "600",
    fontSize: 16,
    color: COLORS.textDark,
  },
  buttonLoading: {
    opacity: 0.8,
  },
    matchedOwnerContainer: {
    marginVertical: 12,
    padding: 12,
    backgroundColor: '#DCF7E3',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#16A34A',
  },
  matchedOwnerLabel: {
    fontWeight: '600',
    marginBottom: 8,
    color: '#166534',
    fontSize: 14,
  },
  matchedOwnerInfo: {
    paddingLeft: 8,
  },
  matchedOwnerName: {
    fontWeight: '700',
    color: '#166534',
    fontSize: 16,
    marginBottom: 4,
  },
  matchedOwnerPhone: {
    color: '#15803D',
    fontSize: 14,
  },
  correctionBadge: {
    backgroundColor: '#FEF3C7',
    color: '#92400E',
    fontSize: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  
  allOwnersContainer: {
    marginVertical: 12,
    padding: 12,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E9ECEF',
  },
  ownersLabel: {
    fontWeight: '600',
    marginBottom: 8,
    color: '#495057',
  },
  ownerItem: {
    marginBottom: 10,
    padding: 10,
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#DEE2E6',
  },
  correctedOwnerItem: {
    backgroundColor: '#FFF7ED',
    borderColor: '#FDBA74',
  },
  ownerName: {
    fontWeight: '500',
    color: '#212529',
    fontSize: 15,
    marginBottom: 2,
  },
  ownerPhone: {
    fontSize: 13,
    color: '#6C757D',
    marginBottom: 4,
  },
  ownerSource: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  ownerSourceText: {
    fontSize: 12,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    overflow: 'hidden',
  },
  originalSource: {
    backgroundColor: '#E9ECEF',
    color: '#495057',
  },
  correctedSource: {
    backgroundColor: '#FEF3C7',
    color: '#92400E',
  },
});

export default FindImmatriculationOrChasisNumberScreen;
